import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all_with_count } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Typography
} from '../../../../components/v2/styled';

import { User } from '../../../../models/User';
import { TreatmentPlan } from '../../../../models/TreatmentPlan';

const { DateTime } = require("luxon");

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

type Props = {
  object: User;
}

export const CoachUserWidgetsBasicTreatment: FunctionComponent<Props> = ({
  object
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  // Pre-format dates as strings (Luxon, same output as <Dt />) so static +
  // value can live in ONE Typography string child, required for the html
  // attribute to engage (it only applies to single-string children).
  const fmtDay = (iso?: string) => {
    return !!iso ? DateTime.fromISO(iso).setZone("Europe/Brussels").toFormat("DDD", {locale: i18n.resolvedLanguage || 'nl'}) : '';
  }
  // const params = useParams();
  // const queryClient = useQueryClient();
  // const { state } = useLocation();
  // const [searchParams, setSearchParams] = useSearchParams();

  // Coach-level widget settings: composite { cta } under one value key.
  // Stored on login.settings.userdetails_widgets.settings.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.basicTreatmentValueKey];
  const showCta = HelpersClientWidgets.getBasicTreatmentCtaEnabled(cfg);

  // Treatment plan (first/only row) for the updated_at + editor stat.
  const {
    data: treatmentPlan,
  } = useQuery({
    queryKey: ["treatment_plans", object?.id],
    queryFn: () =>
      fetch_all_with_count<TreatmentPlan>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'treatment_plan',
          fields: ['id', 'updated_at', 'editor|id/full_name'],
          page: 0,
          per_page: 1,
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
      select: (d) => d.result[0],
    enabled: !!object?.id && !!(login?.practice_id || currentPractice?.id) && object?.type === "User::User",
  });

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* CTA: small text button top-right, absolute in the title row. Gated by
        the coach's `cta` setting. No premium lock, the file page's tabs gate
        their own modules. */}
    {showCta && <Button
      label={t("coach.general.actions.to_treatment_plan")}
      id="goto_treatment"
      text
      size="small"
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        navigate(`/user/${object.id}/file`, {state: {tab: 1}});
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.basic_treatment")}</Typography>
        <Divider />
      </Grid>

      {/* No intake consent: the fixed warning. The body rows below render
          solely in the consented state. */}
      {!object.consent_status?.is_intake && <Grid item xs={12}>
        <Typography sx={{color: theme.palette.warning.main, fontSize: '0.7rem', fontStyle: 'italic'}} html>{t("coach.consents.no_access")}</Typography>
      </Grid>}

      {!!object.consent_status?.is_intake && <>
        {!treatmentPlan && <Grid item xs={12}>
          <Typography variant="body2" sx={{fontStyle: 'italic'}} html>{t("shared.terms.no_results")}</Typography>
        </Grid>}
        {!!treatmentPlan && <Grid item xs={12}>
          <Typography variant="body2" html>{`${t("coach.submenu.treatment_plans.time_updated_short")} ${fmtDay(treatmentPlan.updated_at)}${treatmentPlan.editor_id ? ` ${t("general.terms.by")} ${treatmentPlan.editor_full_name}` : ''}`}</Typography>
        </Grid>}
      </>}

    </Grid>
  </Paper>;
}
